import { useMemo, useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { SkeletonStats, SkeletonTable } from "../../components/ui/Skeleton";
import { useToast } from "../../contexts/ToastContext";
import {
	useBulkNotifyLeasesMutation,
	useCreateLeaseMutation,
	useDeleteLeaseMutation,
	useListLeasesQuery,
	usePatchLeaseMutation,
	type Lease,
} from "../../store/api/leasesApi";
import { useListPropertiesQuery } from "../../store/api/propertiesApi";
import { useListUsersQuery } from "../../store/api/usersApi";
import {
	arrayToCsv,
	downloadCsv,
	formatDateForCsv,
} from "../../utils/csvExport";
import { formatCurrency } from "../../utils/formatCurrency";
import BulkActionsModal, {
	type BulkAction,
} from "./components/BulkActionsModal";
import CreateLeaseModal from "./components/CreateLeaseModal";
import EditLeaseModal from "./components/EditLeaseModal";
import LeaseDetailPanel from "./components/LeaseDetailPanel";
import LeaseFilters from "./components/LeaseFilters";
import LeaseStatsCard from "./components/LeaseStatsCard";
import LeaseTable from "./components/LeaseTable";
import LeaseTimeline from "./components/LeaseTimeline";
import RenewLeaseModal from "./components/RenewLeaseModal";

const LeaseManagement = () => {
	const { success, error: showError } = useToast();
	const [currentView, setCurrentView] = useState<"table" | "timeline">("table");
	const [selectedLeases, setSelectedLeases] = useState<string[]>([]);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [editingLease, setEditingLease] = useState<any | null>(null);
	const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
	const [selectedLease, setSelectedLease] = useState<Lease | null>(null);
	const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
	const [leaseToDelete, setLeaseToDelete] = useState<string | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [leaseToRenew, setLeaseToRenew] = useState<string | null>(null);
	const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [filters, setFilters] = useState({
		status: "",
		expiration: "",
		property: "",
		rentRange: "",
	});

	// Fetch leases from API
	const {
		data: leasesData,
		isLoading: isLoadingLeases,
		error: leasesError,
		refetch: refetchLeases,
	} = useListLeasesQuery({
		page_size: 1000,
		status: filters.status || undefined,
		search: searchTerm || undefined,
		ordering: "-created_at",
	});

	// Fetch properties and users for reference
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 1000 });
	const { data: usersData } = useListUsersQuery({
		page_size: 1000,
		role: "tenant",
	});

	// Mutations
	const [createLease, { isLoading: isCreating }] = useCreateLeaseMutation();
	const [updateLease, { isLoading: isUpdating }] = usePatchLeaseMutation();
	const [deleteLease] = useDeleteLeaseMutation();
	const [bulkNotifyLeases] = useBulkNotifyLeasesMutation();

	// Transform API leases to component format
	const transformedLeases = useMemo(() => {
		if (!leasesData?.results) return [];

		return leasesData.results.map((lease: Lease) => {
			const endDate = new Date(lease.end_date);
			const today = new Date();
			const daysToExpiry = Math.ceil(
				(endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
			);

			const property = propertiesData?.results?.find(
				(p) => p.id === lease.property_id,
			);
			const tenant = usersData?.results?.find((u) => u.id === lease.tenant_id);

			return {
				id: lease.id,
				leaseCode: lease.lease_code || "", // Include auto-generated lease code
				tenant: {
					name:
						lease.tenant_name ||
						`${tenant?.first_name || ""} ${tenant?.last_name || ""}`.trim() ||
						"Unknown",
					email: lease.tenant_email || tenant?.email || "N/A",
					avatar:
						(tenant as any)?.profile_picture || (tenant as any)?.avatar || "",
				},
				property: {
					id: lease.property_id || property?.id || "", // Include property ID for filtering
					name: lease.property_name || property?.name || "Unknown Property",
					unit: property?.address || lease.property_address || "N/A",
					address: lease.property_address || property?.address || "",
				},
				startDate: lease.start_date,
				endDate: lease.end_date,
				rentAmount: Number(lease.monthly_rent) || 0,
				securityDeposit: Number(lease.security_deposit) || 0,
				status: lease.status,
				daysToExpiry,
				term: `${Math.ceil(
					(new Date(lease.end_date).getTime() -
						new Date(lease.start_date).getTime()) /
						(1000 * 60 * 60 * 24 * 30),
				)} months`,
				renewalDeadline: lease.renewal_deadline || "",
				terms: lease.terms || "",
			};
		});
	}, [leasesData, propertiesData, usersData]);

	// Calculate stats from real data
	const stats = useMemo(() => {
		const activeLeases = transformedLeases.filter(
			(lease) => lease.status === "active",
		).length;
		const pendingRenewals = transformedLeases.filter(
			(lease) => lease.status === "draft",
		).length;
		const expiringThisMonth = transformedLeases.filter(
			(lease) =>
				lease.daysToExpiry <= 30 &&
				lease.daysToExpiry > 0 &&
				lease.status === "active",
		).length;
		const totalRevenue = transformedLeases
			.filter((lease) => lease.status === "active")
			.reduce((sum, lease) => sum + lease.rentAmount, 0);

		return {
			activeLeases,
			pendingRenewals,
			expiringThisMonth,
			totalRevenue,
		};
	}, [transformedLeases]);

	// Filter leases based on filters
	const filteredLeases = useMemo(() => {
		let filtered = [...transformedLeases];

		// Apply search (already handled by API, but we can refine client-side)
		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
			filtered = filtered.filter(
				(lease) =>
					lease.tenant.name.toLowerCase().includes(searchLower) ||
					lease.property.name.toLowerCase().includes(searchLower) ||
					lease.property.unit.toLowerCase().includes(searchLower) ||
					lease.id.toLowerCase().includes(searchLower) ||
					(lease.leaseCode &&
						lease.leaseCode.toLowerCase().includes(searchLower)),
			);
		}

		// Apply filters
		if (filters.status) {
			filtered = filtered.filter((lease) => lease.status === filters.status);
		}

		if (filters.expiration) {
			if (filters.expiration === "expired") {
				filtered = filtered.filter((lease) => lease.daysToExpiry < 0);
			} else {
				const days = parseInt(filters.expiration);
				filtered = filtered.filter(
					(lease) => lease.daysToExpiry <= days && lease.daysToExpiry > 0,
				);
			}
		}

		if (filters.property) {
			filtered = filtered.filter(
				(lease) => lease.property.id === filters.property,
			);
		}

		if (filters.rentRange) {
			const [min, max] = filters.rentRange
				.split("-")
				.map((v) => v.replace("+", ""));
			filtered = filtered.filter((lease) => {
				if (max) {
					return (
						lease.rentAmount >= parseInt(min) &&
						lease.rentAmount <= parseInt(max)
					);
				} else {
					return lease.rentAmount >= parseInt(min);
				}
			});
		}

		return filtered;
	}, [transformedLeases, searchTerm, filters]);

	const handleFilterChange = (newFilters: Partial<typeof filters>) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
	};

	const handleSearch = (term: string) => {
		setSearchTerm(term);
	};

	const handleToggleView = (view: "table" | "timeline") => {
		setCurrentView(view);
	};

	interface CreateLeaseData {
		propertyId: string;
		tenantId: string;
		startDate: string;
		endDate: string;
		rentAmount: string | number;
		securityDeposit?: string | number;
		terms?: string;
		documentFiles?: string[]; // Base64 encoded document files
	}

	const handleCreateLease = async (leaseData: CreateLeaseData) => {
		try {
			const leasePayload = {
				// Note: organization is set by backend from user context
				// lease_code is auto-generated by backend
				property_id: leaseData.propertyId,
				tenant_id: leaseData.tenantId,
				start_date: leaseData.startDate,
				end_date: leaseData.endDate,
				monthly_rent: Number(leaseData.rentAmount) || 0,
				security_deposit: leaseData.securityDeposit
					? Number(leaseData.securityDeposit)
					: undefined,
				status: "draft" as const,
				terms: leaseData.terms || "",
				document_files: leaseData.documentFiles || undefined, // Base64 encoded documents
			};

			await createLease(leasePayload).unwrap();
			success("Lease created successfully!");
			await refetchLeases();
			setIsCreateModalOpen(false);
		} catch (error: any) {
			console.error("Error creating lease:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to create lease. Please try again.",
			);
		}
	};

	const handleLeaseClick = (lease: any) => {
		// Find the API lease object to pass to detail panel
		const apiLease = leasesData?.results?.find((l: Lease) => l.id === lease.id);
		if (apiLease) {
			setSelectedLease(apiLease);
			setIsDetailPanelOpen(true);
		}
	};

	const handleEditLease = (leaseId: string) => {
		const lease = transformedLeases.find((l) => l.id === leaseId);
		if (lease) {
			// Find property and tenant IDs from API data for the edit modal
			const apiLease = leasesData?.results?.find(
				(l: Lease) => l.id === leaseId,
			);
			if (apiLease) {
				setEditingLease({
					...lease,
					property: {
						...lease.property,
						id: apiLease.property_id, // This is the property_id from API
					},
					tenant: {
						...lease.tenant,
						id: apiLease.tenant_id, // This is the tenant_id from API
					},
				});
			} else {
				setEditingLease(lease);
			}
			setIsEditModalOpen(true);
		}
	};

	const handleUpdateLease = async (leaseId: string, leaseData: any) => {
		try {
			await updateLease({
				id: leaseId,
				data: leaseData,
			}).unwrap();
			success("Lease updated successfully!");
			await refetchLeases();
			// Refresh detail panel if it's open for this lease
			if (selectedLease?.id === leaseId) {
				const updatedLease = leasesData?.results?.find(
					(l: Lease) => l.id === leaseId,
				);
				if (updatedLease) {
					setSelectedLease(updatedLease);
				}
			}
			setIsEditModalOpen(false);
			setEditingLease(null);
		} catch (error: any) {
			console.error("Error updating lease:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to update lease. Please try again.",
			);
			throw error; // Re-throw to let modal handle it
		}
	};

	const handleRenewLease = async (renewalData: {
		leaseId: string;
		duration: number;
		rentIncrease?: number;
		newRent?: number;
		startDate: string;
		endDate: string;
	}) => {
		try {
			const oldLease = transformedLeases.find(
				(l) => l.id === renewalData.leaseId,
			);
			if (!oldLease) {
				showError("Lease not found");
				return;
			}

			// Mark old lease as renewed
			await updateLease({
				id: renewalData.leaseId,
				data: {
					status: "renewed",
				},
			}).unwrap();

			// Get original lease to access tenant ID
			const originalLease = leasesData?.results?.find(
				(l: Lease) => l.id === renewalData.leaseId,
			);
			if (!originalLease) {
				showError("Original lease data not found");
				return;
			}

			// Create new lease with renewal details
			await createLease({
				property_id: oldLease.property.id,
				tenant_id: originalLease.tenant_id,
				start_date: renewalData.startDate,
				end_date: renewalData.endDate,
				monthly_rent: renewalData.newRent || oldLease.rentAmount,
				security_deposit: oldLease.securityDeposit,
				status: "active",
				terms: oldLease.terms,
			}).unwrap();

			success("Lease renewed successfully!");
			await refetchLeases();
			setIsRenewModalOpen(false);
			setLeaseToRenew(null);
		} catch (error: any) {
			console.error("Error renewing lease:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to renew lease. Please try again.",
			);
		}
	};

	const handleRenewClick = (leaseId: string) => {
		setLeaseToRenew(leaseId);
		setIsRenewModalOpen(true);
	};

	const handleDeleteClick = (leaseId: string) => {
		setLeaseToDelete(leaseId);
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!leaseToDelete) return;

		try {
			await deleteLease(leaseToDelete).unwrap();
			success("Lease deleted successfully!");
			await refetchLeases();
			// Close detail panel if it's open for this lease
			if (selectedLease?.id === leaseToDelete) {
				setIsDetailPanelOpen(false);
				setSelectedLease(null);
			}
			setIsDeleteModalOpen(false);
			setLeaseToDelete(null);
		} catch (error: any) {
			console.error("Error deleting lease:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to delete lease. Please try again.",
			);
		}
	};

	const handleGenerateDocument = (leaseId: string) => {
		const lease = transformedLeases.find((l) => l.id === leaseId);
		if (!lease) {
			showError("Lease not found");
			return;
		}

		try {
			// Generate a simple lease document (PDF-like content)
			const leaseDoc = `
LEASE AGREEMENT

Lease Code: ${lease.leaseCode || "N/A"}
Lease ID: ${lease.leaseCode || lease.id}
Tenant: ${lease.tenant.name}
Property: ${lease.property.name}
Address: ${lease.property.unit}

TERMS:
- Start Date: ${new Date(lease.startDate).toLocaleDateString()}
- End Date: ${new Date(lease.endDate).toLocaleDateString()}
- Monthly Rent: ₦${lease.rentAmount.toLocaleString("en-NG")}
- Security Deposit: ₦${lease.securityDeposit.toLocaleString("en-NG")}
- Term: ${lease.term}
- Status: ${lease.status.toUpperCase()}

${lease.terms ? `ADDITIONAL TERMS:\n${lease.terms}` : ""}

Generated on: ${new Date().toLocaleDateString()}
		`.trim();

			// Create a blob and download
			const blob = new Blob([leaseDoc], { type: "text/plain" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `lease-${lease.leaseCode || lease.id}-${
				new Date().toISOString().split("T")[0]
			}.txt`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			success("Lease document generated successfully!");
		} catch (error) {
			console.error("Error generating document:", error);
			showError("Failed to generate document. Please try again.");
		}
	};

	const handleSendNotification = async (leaseId: string) => {
		const lease = transformedLeases.find((l) => l.id === leaseId);
		if (!lease) {
			showError("Lease not found");
			return;
		}

		try {
			// TODO: Implement backend endpoint for sending notifications
			// For now, show success message
			success(
				`Notification would be sent to ${lease.tenant.name} (${lease.tenant.email})`,
				"Notification Sent",
			);
		} catch (error) {
			console.error("Error sending notification:", error);
			showError("Failed to send notification. Please try again.");
		}
	};

	const handleBulkAction = async (action: BulkAction) => {
		try {
			if (action.type === "renew") {
				// Bulk renewal
				const duration = parseInt(String(action.data.duration || 12));
				const rentIncrease = action.data.rentIncrease
					? parseFloat(String(action.data.rentIncrease))
					: 0;

				const renewalPromises = selectedLeases.map(async (leaseId) => {
					const lease = transformedLeases.find((l) => l.id === leaseId);
					if (!lease) return;

					// Get original lease to access tenant ID
					const originalLease = leasesData?.results?.find(
						(l: Lease) => l.id === leaseId,
					);
					if (!originalLease) return;

					// Calculate new dates
					const currentEndDate = new Date(lease.endDate);
					const startDate = new Date(currentEndDate);
					startDate.setDate(startDate.getDate() + 1);

					const endDate = new Date(startDate);
					endDate.setMonth(endDate.getMonth() + duration);

					// Calculate new rent
					const currentRent = lease.rentAmount || 0;
					const newRent = rentIncrease
						? Math.round(currentRent * (1 + rentIncrease / 100) * 100) / 100
						: currentRent;

					// Mark old lease as renewed
					await updateLease({
						id: leaseId,
						data: { status: "renewed" },
					}).unwrap();

					// Create new lease
					await createLease({
						property_id: lease.property.id,
						tenant_id: originalLease.tenant_id,
						start_date: startDate.toISOString().split("T")[0],
						end_date: endDate.toISOString().split("T")[0],
						monthly_rent: newRent,
						security_deposit: lease.securityDeposit,
						status: "active",
						terms: lease.terms,
					}).unwrap();
				});

				await Promise.all(renewalPromises);
				success(`Successfully renewed ${selectedLeases.length} lease(s)`);
				await refetchLeases();
				setSelectedLeases([]);
				setIsBulkModalOpen(false);
			} else if (action.type === "export") {
				if (!leasesData) return;
				// Export all leases or only selected ones
				const leasesToExport =
					selectedLeases.length > 0
						? transformedLeases.filter((lease) =>
								selectedLeases.includes(lease.id),
						  )
						: transformedLeases;

				const csvData = leasesToExport.map((lease) => {
					// Get original lease for dates
					const originalLease = leasesData?.results?.find(
						(l: Lease) => l.id === lease.id,
					);
					return {
						"Lease ID": lease.leaseCode || lease.id || "",
						"Lease Code": lease.leaseCode || "",
						"Property Address": lease.property.address || "",
						"Tenant Name": lease.tenant.name || "",
						"Start Date": formatDateForCsv(lease.startDate),
						"End Date": formatDateForCsv(lease.endDate),
						"Monthly Rent": lease.rentAmount?.toString() || "0",
						"Security Deposit": lease.securityDeposit?.toString() || "0",
						Status: lease.status || "",
						"Created At": formatDateForCsv(originalLease?.created_at),
						"Updated At": formatDateForCsv(originalLease?.updated_at),
					};
				});

				const csvContent = arrayToCsv(csvData, [
					"Lease ID",
					"Lease Code",
					"Property Address",
					"Tenant Name",
					"Start Date",
					"End Date",
					"Monthly Rent",
					"Security Deposit",
					"Status",
					"Created At",
					"Updated At",
				]);
				downloadCsv(csvContent, "leases-export");
				setIsBulkModalOpen(false);
			} else if (action.type === "delete") {
				await Promise.all(selectedLeases.map((id) => deleteLease(id).unwrap()));
				success(`Successfully deleted ${selectedLeases.length} lease(s)`);
				await refetchLeases();
				setSelectedLeases([]);
				setIsBulkModalOpen(false);
			} else if (
				action.type === "update_status" &&
				(action.data as { status?: string }).status
			) {
				await Promise.all(
					selectedLeases.map((id) =>
						updateLease({
							id,
							data: {
								status: (action.data as { status: string }).status as
									| "active"
									| "expired"
									| "terminated"
									| "draft"
									| "renewed",
							},
						}).unwrap(),
					),
				);
				success(
					`Successfully updated ${selectedLeases.length} lease(s) status`,
				);
				await refetchLeases();
				setSelectedLeases([]);
				setIsBulkModalOpen(false);
			} else if (action.type === "notify") {
				// Bulk notification sending
				const notificationType = action.data.notificationType;
				const customMessage = action.data.customMessage;

				if (!notificationType) {
					showError("Please select a notification type");
					return;
				}

				// Call backend API to send notifications
				const result = await bulkNotifyLeases({
					lease_ids: selectedLeases,
					notification_type: notificationType,
					custom_message: customMessage,
				}).unwrap();

				if (result.success) {
					success(
						result.message ||
							`Notifications sent to ${result.success_count} tenant(s)`,
						"Notifications Sent",
					);

					// Show warning if some failed
					if (result.failed_count > 0) {
						showError(
							`${result.failed_count} notification(s) failed to send. Check console for details.`,
							"Partial Success",
						);
						console.warn("Failed notifications:", result.failed_leases);
					}
				}
				await refetchLeases();
				setSelectedLeases([]);
				setIsBulkModalOpen(false);
			} else if (action.type === "generate-docs") {
				// Bulk document generation
				const documentType = action.data.documentType;
				const emailToTenants = action.data.emailToTenants || false;

				if (!documentType) {
					showError("Please select a document type");
					return;
				}

				const docPromises = selectedLeases.map(async (leaseId) => {
					const lease = transformedLeases.find((l) => l.id === leaseId);
					if (!lease) return;

					// Generate document content based on type
					let docContent = "";
					const timestamp = new Date().toLocaleDateString();

					switch (documentType) {
						case "lease-agreement":
							docContent = `
LEASE AGREEMENT

Lease Code: ${lease.leaseCode || "N/A"}
Lease ID: ${lease.leaseCode || lease.id}
Tenant: ${lease.tenant.name}
Email: ${lease.tenant.email}
Property: ${lease.property.name}
Address: ${lease.property.address}

TERMS:
- Start Date: ${new Date(lease.startDate).toLocaleDateString()}
- End Date: ${new Date(lease.endDate).toLocaleDateString()}
- Monthly Rent: ₦${lease.rentAmount.toLocaleString("en-NG")}
- Security Deposit: ₦${lease.securityDeposit.toLocaleString("en-NG")}
- Term: ${lease.term}
- Status: ${lease.status.toUpperCase()}

${lease.terms ? `ADDITIONAL TERMS:\n${lease.terms}` : ""}

Generated on: ${timestamp}
							`.trim();
							break;

						case "renewal-notice":
							docContent = `
LEASE RENEWAL NOTICE

Lease Code: ${lease.leaseCode || "N/A"}
Tenant: ${lease.tenant.name}
Property: ${lease.property.name}

Dear ${lease.tenant.name},

This notice is to inform you that your lease for ${
								lease.property.name
							} is eligible for renewal.

Current Lease Details:
- End Date: ${new Date(lease.endDate).toLocaleDateString()}
- Monthly Rent: ₦${lease.rentAmount.toLocaleString("en-NG")}

Please contact us to discuss renewal terms.

Generated on: ${timestamp}
							`.trim();
							break;

						case "termination-notice":
							docContent = `
LEASE TERMINATION NOTICE

Lease Code: ${lease.leaseCode || "N/A"}
Tenant: ${lease.tenant.name}
Property: ${lease.property.name}

This notice serves as formal termination of the lease agreement.

Lease Details:
- Property: ${lease.property.name}
- Address: ${lease.property.address}
- Termination Date: ${new Date(lease.endDate).toLocaleDateString()}

Generated on: ${timestamp}
							`.trim();
							break;

						case "rent-receipt":
							docContent = `
RENT RECEIPT

Lease Code: ${lease.leaseCode || "N/A"}
Tenant: ${lease.tenant.name}
Property: ${lease.property.name}
Monthly Rent: ₦${lease.rentAmount.toLocaleString("en-NG")}

Receipt Date: ${timestamp}
							`.trim();
							break;
					}

					// Create and download the document
					const blob = new Blob([docContent], { type: "text/plain" });
					const url = URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;
					link.download = `${documentType}-${lease.leaseCode || lease.id}-${
						new Date().toISOString().split("T")[0]
					}.txt`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(url);

					// TODO: If emailToTenants is true, send via backend API
					if (emailToTenants) {
						// await emailLeaseDocument({ leaseId, documentType }).unwrap();
					}
				});

				await Promise.all(docPromises);
				success(
					`Generated ${selectedLeases.length} document(s)`,
					"Documents Generated",
				);
				setSelectedLeases([]);
				setIsBulkModalOpen(false);
			} else if (action.type === "update-rent") {
				// Bulk rent update
				const increaseType = action.data.increaseType;
				const increaseValue = action.data.increaseValue
					? parseFloat(String(action.data.increaseValue))
					: 0;
				const effectiveDate = action.data.effectiveDate;

				if (!increaseType || !increaseValue) {
					showError("Please provide increase type and value");
					return;
				}

				const rentUpdatePromises = selectedLeases.map(async (leaseId) => {
					const lease = transformedLeases.find((l) => l.id === leaseId);
					if (!lease) return;

					let newRent = lease.rentAmount;

					if (increaseType === "percentage") {
						newRent = Math.round(lease.rentAmount * (1 + increaseValue / 100));
					} else if (increaseType === "fixed") {
						newRent = lease.rentAmount + increaseValue;
					}

					await updateLease({
						id: leaseId,
						data: {
							monthly_rent: newRent,
						},
					}).unwrap();
				});

				await Promise.all(rentUpdatePromises);
				success(
					`Successfully updated rent for ${selectedLeases.length} lease(s)`,
					"Rent Updated",
				);
				await refetchLeases();
				setSelectedLeases([]);
				setIsBulkModalOpen(false);
			} else if (action.type === "extend-lease") {
				// Bulk lease extension
				const extensionPeriod = action.data.extensionPeriod
					? parseInt(String(action.data.extensionPeriod))
					: 0;
				const maintainRent = action.data.maintainRent || false;

				if (!extensionPeriod) {
					showError("Please provide extension period");
					return;
				}

				const extensionPromises = selectedLeases.map(async (leaseId) => {
					const lease = transformedLeases.find((l) => l.id === leaseId);
					if (!lease) return;

					// Calculate new end date
					const currentEndDate = new Date(lease.endDate);
					const newEndDate = new Date(currentEndDate);
					newEndDate.setMonth(newEndDate.getMonth() + extensionPeriod);

					await updateLease({
						id: leaseId,
						data: {
							end_date: newEndDate.toISOString().split("T")[0],
						},
					}).unwrap();
				});

				await Promise.all(extensionPromises);
				success(
					`Successfully extended ${selectedLeases.length} lease(s) by ${extensionPeriod} month(s)`,
					"Leases Extended",
				);
				await refetchLeases();
				setSelectedLeases([]);
				setIsBulkModalOpen(false);
			}
		} catch (error: any) {
			console.error("Error executing bulk action:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to execute bulk action. Please try again.",
			);
		}
	};

	const handleLeaseSelection = (leaseId: string, isSelected: boolean) => {
		if (isSelected) {
			setSelectedLeases((prev) => [...prev, leaseId]);
		} else {
			setSelectedLeases((prev) => prev.filter((id) => id !== leaseId));
		}
	};

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="mb-4 sm:mb-6">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
								Lease Management
							</h1>
							<p className="text-muted-foreground mt-2">
								Manage lease agreements, renewals, and documentation
							</p>
						</div>

						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:flex-shrink-0">
							{selectedLeases.length > 0 && (
								<Button
									variant="outline"
									onClick={() => setIsBulkModalOpen(true)}
									iconName="Settings"
									iconPosition="left"
									className="w-full sm:w-auto"
								>
									Bulk Actions ({selectedLeases.length})
								</Button>
							)}

							<Button
								onClick={() => setIsCreateModalOpen(true)}
								iconName="Plus"
								iconPosition="left"
								className="w-full sm:w-auto"
							>
								Create Lease
							</Button>
						</div>
					</div>
				</div>

				{/* Stats Cards */}
				{isLoadingLeases ? (
					<SkeletonStats count={4} className="mb-4 sm:mb-6" />
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
						<LeaseStatsCard
							title="Active Leases"
							value={stats.activeLeases}
							subtitle="Currently active"
							icon="FileText"
							color="primary"
							trend={{ direction: "up", value: "12%" }}
						/>
						<LeaseStatsCard
							title="Pending Renewals"
							value={stats.pendingRenewals}
							subtitle="Awaiting renewal"
							icon="RefreshCw"
							color="warning"
							trend={{ direction: "up", value: "8%" }}
						/>
						<LeaseStatsCard
							title="Expiring This Month"
							value={stats.expiringThisMonth}
							subtitle="Require attention"
							icon="AlertTriangle"
							color="error"
							trend={undefined}
						/>
						<LeaseStatsCard
							title="Total Monthly Revenue"
							value={formatCurrency(stats.totalRevenue, {
								showAbbreviation: true,
							})}
							subtitle="From active leases"
							icon="Currency"
							color="success"
							trend={{ direction: "up", value: "5%" }}
						/>
					</div>
				)}

				{/* Filters */}
				<LeaseFilters
					onFilterChange={handleFilterChange}
					onSearch={handleSearch}
					onToggleView={handleToggleView}
					currentView={currentView}
				/>

				{/* Loading State */}
				{isLoadingLeases && <SkeletonTable rows={8} columns={6} />}

				{/* Error State */}
				{leasesError && !isLoadingLeases && (
					<div className="bg-card border border-border rounded-lg p-6 sm:p-12 text-center">
						<Icon
							name="AlertCircle"
							size={64}
							className="mx-auto text-error mb-4"
						/>
						<h3 className="text-lg font-medium text-foreground mb-2">
							Error loading leases
						</h3>
						<p className="text-muted-foreground mb-6">
							{leasesError && "data" in leasesError
								? (leasesError.data as any)?.detail || "Failed to load leases"
								: "An error occurred while loading leases"}
						</p>
						<Button
							onClick={() => refetchLeases()}
							iconName="RefreshCw"
							iconPosition="left"
						>
							Retry
						</Button>
					</div>
				)}

				{/* Content Area */}
				{!isLoadingLeases && !leasesError && (
					<>
						{currentView === "table" ? (
							<LeaseTable
								leases={filteredLeases}
								onEditLease={handleEditLease}
								onRenewLease={handleRenewClick}
								onDeleteLease={handleDeleteClick}
								onGenerateDocument={handleGenerateDocument}
								onSendNotification={handleSendNotification}
								onLeaseSelection={handleLeaseSelection}
								selectedLeases={selectedLeases}
								onLeaseClick={handleLeaseClick}
							/>
						) : (
							<LeaseTimeline
								leases={filteredLeases}
								onEditLease={handleEditLease}
								onRenewLease={handleRenewClick}
								onDeleteLease={handleDeleteClick}
								onLeaseClick={handleLeaseClick}
							/>
						)}

						{/* Empty State */}
						{filteredLeases.length === 0 && (
							<div className="bg-card border border-border rounded-lg p-6 sm:p-12 text-center">
								<Icon
									name="FileText"
									size={64}
									className="mx-auto text-muted-foreground mb-4"
								/>
								<h3 className="text-lg font-medium text-foreground mb-2">
									No leases found
								</h3>
								<p className="text-muted-foreground mb-6">
									{searchTerm || Object.values(filters).some((f) => f)
										? "Try adjusting your search or filters"
										: "Get started by creating your first lease agreement"}
								</p>
								{!searchTerm && !Object.values(filters).some((f) => f) && (
									<Button
										onClick={() => setIsCreateModalOpen(true)}
										iconName="Plus"
										iconPosition="left"
									>
										Create First Lease
									</Button>
								)}
							</div>
						)}
					</>
				)}
			</div>

			{/* Modals */}
			<CreateLeaseModal
				isOpen={isCreateModalOpen}
				onClose={() => setIsCreateModalOpen(false)}
				onCreateLease={handleCreateLease}
			/>

			<EditLeaseModal
				isOpen={isEditModalOpen}
				onClose={() => {
					setIsEditModalOpen(false);
					setEditingLease(null);
				}}
				onUpdateLease={handleUpdateLease}
				lease={editingLease}
				isLoading={isUpdating}
			/>

			<BulkActionsModal
				isOpen={isBulkModalOpen}
				onClose={() => setIsBulkModalOpen(false)}
				selectedLeases={selectedLeases}
				onBulkAction={handleBulkAction}
			/>

			<LeaseDetailPanel
				lease={selectedLease}
				isOpen={isDetailPanelOpen}
				onClose={() => {
					setIsDetailPanelOpen(false);
					setSelectedLease(null);
				}}
				onEdit={(lease) => {
					setIsDetailPanelOpen(false);
					handleEditLease(lease.id);
				}}
				onRenew={handleRenewClick}
				onGenerateDocument={handleGenerateDocument}
				onSendNotification={handleSendNotification}
			/>

			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setLeaseToDelete(null);
				}}
				onConfirm={handleConfirmDelete}
				title="Delete Lease"
				message={
					leaseToDelete
						? `Are you sure you want to delete this lease? This action cannot be undone.`
						: ""
				}
				confirmButtonText="Delete"
				isDestructive={true}
			/>

			{/* Renew Lease Modal */}
			<RenewLeaseModal
				isOpen={isRenewModalOpen}
				onClose={() => {
					setIsRenewModalOpen(false);
					setLeaseToRenew(null);
				}}
				lease={
					leaseToRenew
						? leasesData?.results?.find((l: Lease) => l.id === leaseToRenew) ||
						  null
						: null
				}
				onRenew={handleRenewLease}
			/>
		</div>
	);
};

export default LeaseManagement;
